;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

import createStorage from '@/lib/helpers/createStorage'
import { canAccessStorage } from '@/lib/helpers/is-storage-available'
import { PersonalizedResource, ResourceFetchStatus, ResourceState } from '@/lib/types/personalized-resource'

/**
 * Reads the sessionStorage cache for a resource.
 *
 * Runs at module evaluation time, so a malformed value must not throw: two of
 * the four atoms this replaces called `JSON.parse(getItem(key)!)` unguarded,
 * which takes down every module importing them.
 */
function readCachedItems<T>(key: string): T[] | null {
  if (!canAccessStorage('sessionStorage')) return null

  const raw = window.sessionStorage.getItem(key)
  if (raw === null || raw === 'null' || raw === 'undefined') return null

  try {
    return JSON.parse(raw) as T[]
  } catch {
    return null
  }
}

/**
 * Declares a personalized resource. Call at module scope, one per list.
 *
 * Both atoms are created here because the four hand-written pairs had drifted on
 * empty sentinel (`undefined` in three, `null` in one) and on whether the cache
 * read was guarded.
 */
export function personalizedResource<T>(config: {
  key: string
  endpoint: string
  select: (json: unknown) => T[] | null
}): PersonalizedResource<T> {
  const { key, endpoint, select } = config
  const storage = createStorage<T[] | null>('sessionStorage')

  return {
    key,
    endpoint,
    select,
    itemsAtom: atomWithStorage<T[] | null>(key, readCachedItems<T>(key), storage),
    statusAtom: atom<ResourceFetchStatus>('idle'),
  }
}

/**
 * Resolves identity and list data into one state.
 *
 * The precedence is load-order sensitive and reproduces the switch the four
 * renderers each carried:
 *
 *   1. held items win, so a refresh or a failure does not blank a populated list
 *   2. anything in flight, including the profile, shows as loading even before
 *      we know whether anyone is signed in
 *   3. signed in with nothing to show splits into error / idle / empty
 *   4. otherwise there is nobody to show a list for
 */
export function resolveResourceState<T>(
  items: T[] | null,
  status: ResourceFetchStatus,
  isSignedIn: boolean,
  isProfileResolving: boolean,
): ResourceState<T> {
  const isFetching = isProfileResolving || status === 'loading'

  if (isSignedIn && !isFetching && items && items.length > 0) {
    return { status: 'ready', items }
  }

  if (isFetching) return { status: 'loading' }

  if (isSignedIn) {
    if (status === 'failed') return { status: 'error' }
    if (status === 'idle') return { status: 'idle' }
    return { status: 'empty' }
  }

  return { status: 'needsSignIn' }
}
